import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDownRight, Ship, Sprout, Check, X, Waves, Users, Radar, Cpu } from "lucide-react";
import { useLang } from "@/App";
import { MaskedLines, Reveal, Overline, EASE } from "@/components/motion";
import IntroOverlay from "@/components/Intro";
import PSeQ2XrButton from "@/components/PSeQ2XrButton";
import StoryFilm from "@/components/StoryFilm";
import { Pseq2Teaser } from "@/pages/Pseq2";
import { IMAGES } from "@/i18n";

function TelemetryCard({ base = 0 }) {
  const { t } = useLang();
  const [visible, setVisible] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setVisible((v) => (v % t.hero.telemetryLines.length) + 1), 2200);
    return () => clearInterval(id);
  }, [t.hero.telemetryLines.length]);
  return (
    <motion.div
      data-testid="home-oracle-telemetry-badge"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: base + 0.9, ease: EASE }}
      className="w-full max-w-md rounded-2xl border border-lined bg-white/80 p-5 shadow-lg shadow-sky-900/5 backdrop-blur-md"
    >
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-teal">
          {t.hero.telemetryLabel}
        </p>
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-aqua opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-aqua" />
        </span>
      </div>
      <div className="mt-4 flex h-[7.5rem] flex-col justify-end gap-2 overflow-hidden">
        {t.hero.telemetryLines.slice(0, visible).slice(-3).map((line, i, arr) => (
          <p
            key={`${visible}-${i}`}
            className={`telemetry-line font-mono text-[11px] leading-relaxed sm:text-xs ${
              i === arr.length - 1 ? "text-ink" : "text-ink/40"
            }`}
          >
            <span className="mr-2 text-teal">▸</span>
            {line}
          </p>
        ))}
      </div>
      <p className="mt-3 border-t border-lined pt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">
        {t.hero.telemetryLive} · {t.hero.telemetryNote}
      </p>
    </motion.div>
  );
}

function Hero({ base = 0 }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section data-testid="home-hero-section" ref={ref} className="emerald-mesh grain-overlay relative overflow-hidden">
      <div className="grid-pattern absolute inset-0" aria-hidden="true" />
      <div className="hero-glow absolute inset-0" aria-hidden="true" />
      <div className="ambient-drift absolute -left-32 top-24 h-96 w-96 rounded-full bg-aqua/10 blur-3xl" aria-hidden="true" />
      <div className="ambient-drift-slow absolute -right-24 bottom-0 h-[28rem] w-[28rem] rounded-full bg-teal/30 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-20 sm:px-6 sm:pt-28 lg:px-8">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: base + 0.05 }}
              className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-teal"
            >
              {t.hero.overline}
            </motion.p>
            <MaskedLines
              lines={t.hero.lines}
              className="mt-6 font-headline text-4xl font-extrabold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
              lineClassName=""
              delay={base + 0.15}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: base + 0.75, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg"
            >
              {t.hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: base + 0.9, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                data-testid="home-cta-licensing-btn"
                to="/licensing#request-form"
                className="group inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                data-testid="home-cta-how-it-works-btn"
                to="/how-it-works"
                className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 font-headline text-sm font-semibold text-ink transition-colors duration-300 hover:border-teal hover:text-teal"
              >
                {t.hero.ctaSecondary}
                <ArrowDownRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
          <TelemetryCard base={base} />
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: base + 0.5, ease: EASE }}
          className="clipped-frame relative overflow-hidden rounded-t-3xl"
        >
          <motion.img
            src={IMAGES.microalgae_pond_aerial}
            alt={t.hero.imageCaption}
            style={{ y: imgY, scale: 1.15 }}
            className="h-72 w-full object-cover sm:h-96 lg:h-[30rem]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-deep/70 via-transparent to-transparent" aria-hidden="true" />
          <p className="absolute bottom-5 left-6 font-mono text-[11px] uppercase tracking-[0.2em] text-bone/90">
            {t.hero.imageCaption}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function ValueProposition() {
  const { t } = useLang();
  return (
    <section data-testid="home-valprop-section" className="bg-bone py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>{t.valprop.overline}</Overline>
          <h2 className="mt-4 max-w-2xl font-headline text-2xl font-bold tracking-tight text-deep sm:text-3xl lg:text-4xl">
            {t.valprop.title}
          </h2>
        </Reveal>
        <div data-testid="supply-chain-comparison-grid" className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-lined bg-white p-8 sm:p-10">
              <div className="flex items-center gap-3">
                <span className="rounded-xl bg-ink/5 p-3 text-ink/60">
                  <Ship className="h-6 w-6" />
                </span>
                <h3 className="font-headline text-xl font-bold text-ink/70">{t.valprop.imported.title}</h3>
              </div>
              <ul className="mt-7 space-y-4">
                {t.valprop.imported.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-ink/60">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-ink/35" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="relative h-full overflow-hidden rounded-3xl border border-teal/25 bg-deep p-8 text-bone sm:p-10">
              <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-aqua/15 blur-3xl" aria-hidden="true" />
              <div className="flex items-center gap-3">
                <span className="rounded-xl bg-aqua/15 p-3 text-aqua">
                  <Sprout className="h-6 w-6" />
                </span>
                <h3 className="font-headline text-xl font-bold">{t.valprop.local.title}</h3>
              </div>
              <ul className="mt-7 space-y-4">
                {t.valprop.local.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-bone/85">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-aqua" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Bento() {
  const { t } = useLang();
  const b = t.bento.items;
  return (
    <section className="bg-bone pb-20 sm:pb-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>{t.bento.overline}</Overline>
          <h2 className="mt-4 max-w-2xl font-headline text-2xl font-bold tracking-tight text-deep sm:text-3xl lg:text-4xl">
            {t.bento.title}
          </h2>
        </Reveal>
        <div data-testid="home-bento-grid" className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          <Reveal className="md:col-span-7" delay={0.05}>
            <div data-testid="bento-item-ponds" className="group relative h-full min-h-[20rem] overflow-hidden rounded-3xl">
              <img
                src={IMAGES.hero_aquaculture}
                alt={b.ponds.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-deep/90 via-deep/25 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 p-8">
                <Waves className="mb-3 h-6 w-6 text-aqua" />
                <h3 className="font-headline text-xl font-bold text-bone sm:text-2xl">{b.ponds.title}</h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-bone/80">{b.ponds.desc}</p>
              </div>
            </div>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.12}>
            <div data-testid="bento-item-coops" className="card-glow h-full min-h-[20rem] rounded-3xl border border-lined bg-white p-8 transition-shadow duration-500 hover:shadow-xl hover:shadow-teal/10">
              <span className="inline-flex rounded-xl bg-teal/10 p-3 text-teal">
                <Users className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-headline text-xl font-bold text-deep sm:text-2xl">{b.coops.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink/65">{b.coops.desc}</p>
            </div>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.05}>
            <div data-testid="bento-item-sensors" className="group relative h-full min-h-[20rem] overflow-hidden rounded-3xl">
              <img
                src={IMAGES.coastal_buoy_sensors}
                alt={b.sensors.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-deep/90 via-deep/25 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 p-8">
                <Radar className="mb-3 h-6 w-6 text-aqua" />
                <h3 className="font-headline text-xl font-bold text-bone sm:text-2xl">{b.sensors.title}</h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-bone/80">{b.sensors.desc}</p>
              </div>
            </div>
          </Reveal>
          <Reveal className="md:col-span-7" delay={0.12}>
            <div data-testid="bento-item-oracle" className="grain-overlay relative h-full min-h-[20rem] overflow-hidden rounded-3xl bg-deep p-8">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-aqua/10 blur-3xl" aria-hidden="true" />
              <span className="inline-flex rounded-xl bg-aqua/15 p-3 text-aqua">
                <Cpu className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-headline text-xl font-bold text-bone sm:text-2xl">{b.oracle.title}</h3>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-bone/75">{b.oracle.desc}</p>
              <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-aqua/80">
                sensor → oracle → carbon point
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Calculator() {
  const { t } = useLang();
  const [ha, setHa] = useState(6);
  const readings = ha * 144;
  const points = ha * 6;
  const index = ha * 10;
  return (
    <section data-testid="home-calculator-section" className="bg-bone py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <Overline>{t.calc.overline}</Overline>
            <h2 className="mt-4 font-headline text-2xl font-bold tracking-tight text-deep sm:text-3xl lg:text-4xl">
              {t.calc.title}
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-ink/65">{t.calc.desc}</p>
            <div className="mt-10">
              <label htmlFor="hectares-slider" className="flex items-baseline justify-between font-mono text-xs font-semibold uppercase tracking-[0.18em] text-teal">
                <span>{t.calc.sliderLabel}</span>
                <span className="font-headline text-3xl font-extrabold tracking-tight text-deep">
                  {ha} <span className="text-base font-bold text-teal">{t.calc.unit}</span>
                </span>
              </label>
              <input
                data-testid="calculator-hectares-slider"
                id="hectares-slider"
                type="range"
                min="1"
                max="20"
                value={ha}
                onChange={(e) => setHa(Number(e.target.value))}
                className="mt-4 w-full"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div data-testid="calculator-output-card" className="rounded-3xl border border-lined bg-white p-8 sm:p-10">
              {[
                { label: t.calc.outputs.volume, value: index },
                { label: t.calc.outputs.datapoints, value: readings },
                { label: t.calc.outputs.points, value: points },
              ].map((o, i) => (
                <div key={i} className={`flex items-baseline justify-between py-4 ${i > 0 ? "border-t border-lined" : ""}`}>
                  <span className="text-sm text-ink/60">{o.label}</span>
                  <span className="font-headline text-2xl font-extrabold tracking-tight text-teal sm:text-3xl">
                    {o.value.toLocaleString()}
                  </span>
                </div>
              ))}
              <p className="mt-6 rounded-xl bg-bone p-4 font-mono text-[11px] leading-relaxed text-ink/50">
                {t.calc.disclaimer}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function XrSection() {
  const { t } = useLang();
  return (
    <section data-testid="home-xr-section" className="bg-bone pb-20 sm:pb-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-sky-300/50 bg-gradient-to-br from-sky-100 via-white to-sky-200 p-8 shadow-2xl shadow-sky-900/10 sm:p-12">
            <div className="grid-pattern absolute inset-0 opacity-60" aria-hidden="true" />
            <div className="hero-glow absolute inset-0" aria-hidden="true" />
            <div data-testid="xr-scanline" className="xr-scanline" aria-hidden="true" />
            <div className="relative flex flex-col items-center gap-8 lg:flex-row lg:justify-between">
              <div className="max-w-xl text-center lg:text-left">
                <Overline>{t.xr.overline}</Overline>
                <h2 className="mt-4 font-headline text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                  {t.xr.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-ink/70">{t.xr.sub}</p>
              </div>
              <div className="w-full max-w-sm shrink-0">
                <PSeQ2XrButton />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CtaSection() {
  const { t } = useLang();
  return (
    <section className="emerald-mesh grain-overlay relative overflow-hidden">
      <div className="hero-glow absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 sm:py-28 lg:px-8">
        <Reveal>
          <h2 className="mx-auto max-w-2xl font-headline text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            {t.ctaSection.title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink/70">{t.ctaSection.desc}</p>
          <Link
            data-testid="home-bottom-cta-btn"
            to="/licensing#request-form"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep"
          >
            {t.ctaSection.btn}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

let introShownThisLoad = false;

export default function Home() {
  const { t } = useLang();
  const [showIntro, setShowIntro] = useState(() => !introShownThisLoad);

  const finishIntro = () => {
    introShownThisLoad = true;
    setShowIntro(false);
  };

  return (
    <>
      <AnimatePresence>
        {showIntro && <IntroOverlay key="intro" onFinish={finishIntro} />}
      </AnimatePresence>
      <Hero base={showIntro ? 1.9 : 0} />
      <Pseq2Teaser />
      <ValueProposition />
      <StoryFilm />
      <Bento />
      <Calculator />
      <XrSection />
      <CtaSection />
    </>
  );
}
